import { Badge, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconArrowRight, IconSwitchHorizontal } from "@tabler/icons-react";
import { useState } from "react";
import { Control, UseFormGetValues, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CompanySettings } from "#modules/company/infra/CompanySettings.dto";
import { useUpdateCompanySetting } from "#modules/company/view/useUpdateCompanySetting";
import { useMyModel } from "#modules/models/view/useModel";
import { SELECTABLE_PART_TYPES } from "#shared/helpers/partTypeOptions";
import { partTypesWordingMap } from "#shared/helpers/PartTypesWordingMap";
import { CheckboxController } from "#shared/view/components/CheckboxController";
import { Grid } from "#shared/view/components/Grid";
import { Group } from "#shared/view/components/Group";
import { Modal } from "#shared/view/components/Modal";
import { PriceInputController } from "#shared/view/components/PriceInputController";
import { Stack } from "#shared/view/components/Stack";
import { TextareaController } from "#shared/view/components/TextareaController";
import { Title } from "#shared/view/components/Title";
import { Button } from "#shared/view/designSystem/Button";
import { NativeSelectInputController } from "#shared/view/designSystem/inputs/NativeSelectInputController";
import { NumberInputController } from "#shared/view/designSystem/inputs/NumberInputController";
import { TextInput } from "#shared/view/designSystem/inputs/TextInput";
import { UnitName } from "#shared/view/designSystem/inputs/units";
import { NativeSelectInput } from "#shared/view/designSystem/NativeSelectInput";
import {
  AssemblySplitModeSchema,
  CompanyTypeSchema,
  formatTimeString,
  PartTypeSchema,
  PartTypeType,
  PredictionModelTypeSchema,
  TimeDisplayFormatSchema,
  TransportCalculationMethodSchema,
} from "shared";

export const CompanyTypeSection = ({ control, getValues }: SectionProps) => {
  const { t } = useTranslation();

  const fields: SectionField[] = [
    {
      name: "companyType",
      label: t("settings.partType.label"),
      type: "select",
      data: [
        {
          label: t("settings.partType.automatic"),
          value: "",
        },
        {
          label: t("settings.partType.sheet"),
          value: CompanyTypeSchema.enum.SHEET,
        },
        {
          label: t("settings.partType.machining"),
          value: CompanyTypeSchema.enum.MACHINING,
        },
        {
          label: t("settings.partType.turning"),
          value: CompanyTypeSchema.enum.TURNING,
        },
      ],
    },
    {
      name: "assemblySplitMode",
      label: "Type d'assemblage",
      type: "select",
      data: [
        {
          label: "Pièces uniquement",
          value: AssemblySplitModeSchema.enum.LEAF,
        },
        {
          label: "Pièces + sous-ensembles",
          value: AssemblySplitModeSchema.enum.ALL,
        },
        {
          label: "Pas de découpage de l'assemblage",
          value: AssemblySplitModeSchema.enum.ROOT,
        },
      ],
    },
    {
      name: "isBetaTester",
      label: "Fonctionnalités expérimentales",
      type: "checkbox",
    },
    {
      name: "forceCompanyTypeForConsumables",
      label: "Forcer le type de pièce pour les consommables",
      type: "checkbox",
      shouldHide: () => (getValues ? !getValues("companyType") : false),
    },
    {
      name: "authorizeConsumable",
      label: "Autoriser la détection des consommables",
      type: "checkbox",
    },
  ];

  return <Section title="Typologie" fields={fields} control={control} />;
};

export const ModelTypeSection = ({ control }: SectionProps) => {
  const { model } = useMyModel();

  return (
    <Stack gap={0}>
      <Title order={5}>{"Modèle de prédiction de coût"}</Title>
      <Group>
        <TextInput
          disabled
          value={
            model
              ? model.type === PredictionModelTypeSchema.enum.CLUSTERING
                ? "Modèle par similarité"
                : "Modèle spécifique"
              : "Modèle générique"
          }
        />
        <CheckboxController
          label="Afficher les costs drivers personnalisés"
          name="hasCustomInputs"
          control={control}
        />
      </Group>
    </Stack>
  );
};

export const SearchResultSection = ({ control }: SectionProps) => {
  const fields: SectionField[] = [
    {
      name: "shouldDisplayChildrenAsSearchResults",
      label: "Montrer les enfants des assemblages",
      type: "checkbox",
    },
    {
      name: "similarityThreshold",
      label: "Seuil de similarité",
      type: "number",
      inputProps: {
        min: 0,
        max: 1,
        step: 0.01,
        precision: 2,
        description: "Valeur entre 0 et 1 (1 = 100% de similarité)",
      },
    },
  ];

  return <Section title="Résultats de recherche" fields={fields} control={control} />;
};

export const DefaultMarginsSection = ({ control }: SectionProps) => {
  const { t } = useTranslation();

  const fields: SectionField[] = [
    {
      name: "materialMarginRatio",
      label: "Taux de marge matière",
      type: "number",
      unit: "%",
    },
    {
      name: "addedValueProcessMarginRatio",
      label: "Taux de marge valeur ajoutée",
      type: "number",
      unit: "%",
    },
    {
      name: "productsProcessMarginRatio",
      label: "Taux de marge achats",
      type: "number",
      unit: "%",
    },
  ];

  return <Section title={t("settings.defaultMargins")} fields={fields} control={control} />;
};

export const SalesMarginSection = ({ control }: SectionProps) => {
  const fields: SectionField[] = [
    {
      name: "salesMarginRatio",
      label: "Taux de marge commerciale",
      type: "number",
      unit: "%",
    },
  ];

  return <Section title="Marge commerciale" fields={fields} control={control} />;
};

export const DefaultTransportSection = ({ control }: SectionProps) => {
  const fields: SectionField[] = [
    {
      name: "defaultTransportCalculationMethod",
      label: "Méthode de calcul par défaut",
      type: "select",
      data: [
        {
          label: "Coût forfaitaire",
          value: TransportCalculationMethodSchema.enum.FIXED,
        },
        {
          label: "Au poids",
          value: TransportCalculationMethodSchema.enum.WEIGHT,
        },
      ],
    },
  ];

  return <Section title="Transport" fields={fields} control={control} />;
};

export const FinalizationSection = ({ control }: SectionProps) => {
  const fields: SectionField[] = [
    {
      name: "quoteExpirationDelay",
      label: "Jours de validité d'un devis",
      type: "number",
      inputProps: {
        precision: 0,
      },
      unit: "jours",
    },
    {
      name: "quoteComment",
      label: "Commentaire de bas de devis",
      type: "textarea",
      placeholder:
        'ex: Signature du client ou de son représentant précédée de la mention "Bon pour accord" et date :',
    },
  ];

  return <Section title="Finalisation du devis" fields={fields} control={control} />;
};

export const DisplaySection = ({ control }: SectionProps) => {
  const { t } = useTranslation();

  const fields: SectionField[] = [
    {
      name: "timeDisplayFormat",
      label: t("settings.display.timeDisplayFormat.label"),
      type: "select",
      data: TimeDisplayFormatSchema.options.map((option) => ({
        label: t(`settings.display.timeDisplayFormat.options.${option}`, {
          example: formatTimeString(18.87, option),
        }),
        value: option,
      })),
    },
  ];

  return <Section title={t("settings.display.title")} fields={fields} control={control} />;
};

export const PartTypeMapperOverrideSection = () => {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure(false);
  const { setValue, getValues } = useFormContext<CompanySettings>();
  const { updateCompanySetting } = useUpdateCompanySetting({ shouldNotNotify: true });

  const [localValue, setLocalValue] = useState<Partial<Record<PartTypeType, PartTypeType>>>(
    () =>
      (getValues("partTypeMapperOverride") as Partial<Record<PartTypeType, PartTypeType>> | null) ??
      {},
  );

  const activeCount = Object.keys(localValue).length;

  const targetOptions = [
    { value: "", label: "Automatique" },
    ...Array.from(SELECTABLE_PART_TYPES).map((type) => ({
      value: type,
      label: partTypesWordingMap[type],
    })),
  ];

  const handleChange = (fromType: PartTypeType, toType: string) => {
    const updated = { ...localValue };
    if (!toType) {
      delete updated[fromType];
    } else {
      updated[fromType] = PartTypeSchema.parse(toType);
    }
    const newValue = Object.keys(updated).length === 0 ? null : updated;
    setLocalValue(updated);
    setValue("partTypeMapperOverride", newValue);
    updateCompanySetting({
      partTypeMapperOverride: newValue as Record<PartTypeType, PartTypeType>,
    });
  };

  return (
    <>
      <Group gap="xs">
        <Button
          variant="subtle"
          size="sm"
          leftSection={<IconSwitchHorizontal size={14} />}
          onClick={open}
        >
          Correspondances de types de pièces
        </Button>
        {activeCount > 0 && (
          <Badge size="sm" variant="light">
            {t("settings.partTypeMapperOverride.activeRules", { count: activeCount })}
          </Badge>
        )}
      </Group>

      <Modal opened={opened} onClose={close} title="Correspondances de types de pièces" size="xl">
        <Text size="sm" c="dimmed" mb="md">
          {
            "Redirige un type détecté vers un autre. Prime sur le type de pièce par défaut. Le type choisi manuellement par l'utilisateur reste prioritaire."
          }
        </Text>
        <Grid>
          {Array.from(SELECTABLE_PART_TYPES).map((fromType) => (
            <Grid.Col key={fromType} span={6}>
              <Group gap="xs" align="center" wrap="nowrap">
                <Text size="sm" style={{ flex: 1, minWidth: 0 }} truncate="end">
                  {partTypesWordingMap[fromType]}
                </Text>
                <IconArrowRight size={12} color="gray" style={{ flexShrink: 0 }} />
                <NativeSelectInput
                  size="sm"
                  data={targetOptions}
                  value={localValue[fromType] ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleChange(fromType, e.target.value)
                  }
                  withEmptyData={false}
                />
              </Group>
            </Grid.Col>
          ))}
        </Grid>
        <Group justify="flex-end" mt="md">
          <Button onClick={close}>Fermer</Button>
        </Group>
      </Modal>
    </>
  );
};

export type SectionProps = {
  control: Control<CompanySettings>;
  getValues?: UseFormGetValues<CompanySettings>;
  title?: string;
  fields?: SectionField[];
};

type SectionField = {
  name: keyof CompanySettings;
  label: string;
  type: "number" | "price" | "select" | "textarea" | "checkbox";
  unit?: UnitName;
  data?: {
    value: string;
    label: string;
  }[];
  inputProps?: Record<string, unknown>;
  placeholder?: string;
  shouldHide?: () => boolean;
};

const Section = ({ title, fields, control }: SectionProps) => {
  return (
    <Stack gap={0}>
      <Title order={5}>{title}</Title>

      <Group align={"flex-end"} gap="xs">
        {fields
          ?.filter((field) => field.type !== "textarea")
          .map((field) =>
            field.shouldHide && field.shouldHide() ? null : (
              <FormField key={field.name} field={field} control={control} />
            ),
          )}
      </Group>
      {fields
        ?.filter((field) => field.type === "textarea")
        .map((field) =>
          field.shouldHide && field.shouldHide() ? null : (
            <FormField key={field.name} field={field} control={control} />
          ),
        )}
    </Stack>
  );
};

const FormField = ({
  field,
  control,
}: {
  field: SectionField;
  control: Control<CompanySettings>;
}) => {
  switch (field.type) {
    case "number":
      return (
        <NumberInputController
          name={field.name}
          label={field.label}
          control={control}
          unit={field.unit}
          {...field.inputProps}
        />
      );

    case "price":
      return (
        <PriceInputController
          name={field.name}
          label={field.label}
          control={control}
          unit={field.unit}
        />
      );

    case "select":
      return (
        <NativeSelectInputController
          name={field.name}
          data={field.data || []}
          label={field.label}
          control={control}
          withEmptyData={false}
          {...field.inputProps}
        />
      );

    case "textarea":
      return (
        <TextareaController
          name={field.name}
          label={field.label}
          control={control}
          maw="50%"
          minRows={3}
          placeholder={field.placeholder}
          {...field.inputProps}
        />
      );

    case "checkbox":
      return (
        <CheckboxController
          name={field.name}
          label={field.label}
          control={control}
          {...field.inputProps}
        />
      );

    default:
      return null;
  }
};
